import React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ScrollReveal from "@/components/ui/scroll-reveal";
import { Download, BarChart3, FileText, Check, ChartBar } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";

const AnalyticsReporting = () => {
  const { t } = useTranslation();

  const dailyData = [
    { day: "Mon", calls: 12, answered: 10, rate: "83%" },
    { day: "Tue", calls: 19, answered: 15, rate: "79%" },
    { day: "Wed", calls: 15, answered: 13, rate: "87%" },
    { day: "Thu", calls: 22, answered: 18, rate: "82%" },
    { day: "Fri", calls: 25, answered: 22, rate: "88%" },
    { day: "Sat", calls: 18, answered: 15, rate: "83%" },
    { day: "Sun", calls: 10, answered: 9, rate: "90%" },
  ];
  
  const weeklyData = [
    { day: "Week 1", calls: 85, answered: 70, rate: "82%" },
    { day: "Week 2", calls: 95, answered: 80, rate: "84%" },
    { day: "Week 3", calls: 75, answered: 65, rate: "87%" },
    { day: "Week 4", calls: 110, answered: 95, rate: "86%" },
  ];
  
  const monthlyData = [
    { day: "Jan", calls: 320, answered: 280, rate: "88%" },
    { day: "Feb", calls: 350, answered: 310, rate: "89%" },
    { day: "Mar", calls: 410, answered: 370, rate: "90%" },
    { day: "Apr", calls: 380, answered: 340, rate: "89%" },
    { day: "May", calls: 420, answered: 390, rate: "93%" },
  ];

  const [activeTimePeriod, setActiveTimePeriod] = React.useState("monthly");
  
  const getActiveData = () => {
    switch (activeTimePeriod) {
      case "daily":
        return dailyData;
      case "weekly":
        return weeklyData;
      case "monthly":
        return monthlyData;
      default:
        return monthlyData;
    }
  };

  const callTranscriptData = [
    { 
      time: "09:01",
      agent: "Agent Ripken:",
      transcript: "Hello, this is Ripken from Rideplay."
    },
    { 
      time: "09:02",
      agent: "Sandeep:",
      transcript: "Hello! This is Sandeep speaking."
    },
    { 
      time: "09:04",
      agent: "Agent Ripken:",
      transcript: "I'm calling about your recent inquiry about our services."
    },
    { 
      time: "09:06",
      agent: "Sandeep:",
      transcript: "Yes, I was interested in learning more about your solutions."
    }
  ];

  return (
    <section className="py-12 md:py-20 bg-background text-foreground">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
          {/* Left side - Analytics Dashboard */}
          <ScrollReveal className="flex-1 w-full">
            <Card className="bg-card border-border overflow-hidden rounded-xl shadow-lg">
              <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
                  <h3 className="text-lg md:text-xl font-medium text-foreground flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2 text-brand-600" />
                    {t("analytics.callAnalytics")}
                  </h3>
                  <div className="flex gap-1 sm:gap-2">
                    <Button 
                      variant={activeTimePeriod === "daily" ? "default" : "outline"} 
                      size="sm" 
                      className={`text-xs sm:text-sm ${activeTimePeriod === "daily" ? "bg-brand-500 text-white" : "border-border bg-card text-foreground hover:bg-muted"}`}
                      onClick={() => setActiveTimePeriod("daily")}
                    >
                      {t("analytics.daily")}
                    </Button>
                    <Button 
                      variant={activeTimePeriod === "weekly" ? "default" : "outline"} 
                      size="sm" 
                      className={`text-xs sm:text-sm ${activeTimePeriod === "weekly" ? "bg-brand-500 text-white" : "border-border bg-card text-foreground hover:bg-muted"}`}
                      onClick={() => setActiveTimePeriod("weekly")}
                    >
                      {t("analytics.weekly")}
                    </Button>
                    <Button 
                      variant={activeTimePeriod === "monthly" ? "default" : "outline"} 
                      size="sm" 
                      className={`text-xs sm:text-sm ${activeTimePeriod === "monthly" ? "bg-brand-500 text-white" : "border-border bg-card text-foreground hover:bg-muted"}`}
                      onClick={() => setActiveTimePeriod("monthly")}
                    >
                      {t("analytics.monthly")}
                    </Button>
                  </div>
                </div>
                
                {/* Chart */}
                <div className="h-48 sm:h-64 mb-6 md:mb-8 bg-card p-2 sm:p-3 rounded-lg border border-border">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={getActiveData()}
                      margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="day" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                      <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                      <Tooltip 
                        formatter={(value, name) => [value, name === "calls" ? "Total Calls" : "Answered Calls"]}
                        labelFormatter={(label) => `${label}`}
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '0.5rem',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                        }}
                        cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                      />
                      <Legend 
                        formatter={(value) => value === "calls" ? "Total Calls" : "Answered Calls"}
                        iconType="circle"
                        wrapperStyle={{ fontSize: '12px' }}
                      />
                      <Bar 
                        dataKey="calls" 
                        fill="#7e66ff" 
                        radius={[4, 4, 0, 0]} 
                        name="calls" 
                        barSize={20}
                      />
                      <Bar 
                        dataKey="answered" 
                        fill="#4f2fb3" 
                        radius={[4, 4, 0, 0]} 
                        name="answered" 
                        barSize={20}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Stats Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4 md:mb-6">
                  {getActiveData().slice(0, 4).map((item, index) => (
                    <div key={index} className="bg-muted border border-border rounded-lg p-2 sm:p-3 text-center">
                      <div className="text-xs sm:text-sm text-muted-foreground">{item.day}</div>
                      <div className="font-semibold text-base sm:text-lg text-brand-700 dark:text-brand-400">{item.rate}</div>
                      <div className="text-xs text-muted-foreground">{t("analytics.responseRate")}</div>
                    </div>
                  ))}
                </div>
                
                {/* Transcript Panel - Hidden on small mobile */}
                <div className="mb-4 md:mb-6 hidden sm:block">
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <h3 className="text-base md:text-lg font-medium text-foreground">{t("analytics.recentConversation")}</h3>
                    <Button variant="outline" size="sm" className="text-brand-600 border-brand-200 hover:bg-brand-50 dark:hover:bg-brand-900/20 text-xs sm:text-sm">
                      <FileText size={14} className="mr-1" /> {t("analytics.viewAll")}
                    </Button>
                  </div>
                  
                  <Card className="bg-muted border-border rounded-lg p-3 sm:p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="bg-brand-100 dark:bg-brand-900/30 text-brand-600 text-xs px-2 py-1 rounded">{t("analytics.incoming")}</span>
                      <span className="text-xs sm:text-sm text-muted-foreground">Today, 09:01 AM</span>
                      <span className="bg-green-100 dark:bg-green-900/30 text-green-600 text-xs px-2 py-1 rounded sm:ml-auto">{t("analytics.positiveSentiment")}</span>
                    </div>
                    
                    <div className="h-32 sm:h-48 overflow-y-auto text-xs space-y-2 sm:space-y-3 pr-2 scrollbar-thin bg-card p-2 sm:p-3 rounded-md border border-border">
                      {callTranscriptData.map((entry, index) => (
                        <div key={index} className={`space-y-1 ${entry.agent.includes('Agent') ? 'pl-3 sm:pl-4 border-l-2 border-brand-300' : 'pl-3 sm:pl-4 border-l-2 border-muted-foreground/30'}`}>
                          <div className={`text-xs ${entry.agent.includes('Agent') ? 'text-brand-600' : 'text-muted-foreground'}`}>
                            {entry.time} {entry.agent}
                          </div>
                          <div className="text-foreground bg-muted p-1.5 sm:p-2 rounded-md text-xs">{entry.transcript}</div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                      <div>
                        <span className="text-xs text-muted-foreground">{t("analytics.callDuration")}:</span>
                        <span className="ml-2 text-sm text-brand-600">00:02:15</span>
                      </div>
                      <Button variant="outline" size="sm" className="text-brand-600 border-brand-200 hover:bg-brand-50 dark:hover:bg-brand-900/20 text-xs">
                        <Download size={14} className="mr-1" /> {t("analytics.export")}
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
            </Card>
          </ScrollReveal>

          {/* Right side - Analytics Features */}
          <ScrollReveal className="flex-1 w-full" delay={200}>
            <div className="max-w-xl">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6 text-foreground">
                {t("analytics.title")}
              </h2>
              
              <p className="text-sm sm:text-base text-muted-foreground mb-6 md:mb-8">
                {t("analytics.subtitle")}
              </p>
              
              <div className="mb-6 md:mb-8 bg-muted p-4 sm:p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-brand-600">{t("analytics.performanceMetrics.title")}</h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-4">
                  {t("analytics.performanceMetrics.subtitle")}
                </p>
                
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-brand-100 dark:bg-brand-900/30">
                    <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-brand-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground text-sm sm:text-base">{t("analytics.performanceMetrics.advancedAnalytics")}</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground">{t("analytics.performanceMetrics.advancedAnalyticsDesc")}</p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div className="bg-card p-2 sm:p-3 rounded border border-border">
                    <div className="text-xs text-muted-foreground">{t("analytics.performanceMetrics.avgResponse")}</div>
                    <div className="text-sm sm:text-base font-semibold text-brand-600">87%</div>
                  </div>
                  <div className="bg-card p-2 sm:p-3 rounded border border-border">
                    <div className="text-xs text-muted-foreground">{t("analytics.performanceMetrics.callsMonth")}</div>
                    <div className="text-sm sm:text-base font-semibold text-brand-600">1,240</div>
                  </div>
                  <div className="bg-card p-2 sm:p-3 rounded border border-border">
                    <div className="text-xs text-muted-foreground">{t("analytics.performanceMetrics.avgDuration")}</div>
                    <div className="text-sm sm:text-base font-semibold text-brand-600">2m 15s</div>
                  </div>
                </div>
              </div>
              
              <div className="mb-6 md:mb-8 bg-muted p-4 sm:p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-brand-600">{t("analytics.transcripts.title")}</h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-4">
                  {t("analytics.transcripts.subtitle")}
                </p>
                
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-brand-100 dark:bg-brand-900/30">
                    <ChartBar className="h-5 w-5 sm:h-6 sm:w-6 text-brand-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground text-sm sm:text-base">{t("analytics.transcripts.sentimentAnalysis")}</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground">{t("analytics.transcripts.sentimentAnalysisDesc")}</p>
                  </div>
                </div>

                <div className="mt-4 bg-card p-2 sm:p-3 rounded border border-border">
                  <div className="flex items-center justify-between">
                    <div className="text-xs sm:text-sm font-medium text-foreground">{t("analytics.transcripts.breakdown")}</div>
                    <div className="text-xs text-muted-foreground">{t("analytics.transcripts.last30Days")}</div>
                  </div>
                  
                  <div className="flex items-center mt-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: '65%' }}></div>
                    </div>
                    <span className="ml-2 text-xs text-muted-foreground">65% {t("analytics.transcripts.positive")}</span>
                  </div>
                  
                  <div className="flex items-center mt-1">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-400 rounded-full" style={{ width: '28%' }}></div>
                    </div>
                    <span className="ml-2 text-xs text-muted-foreground">28% {t("analytics.transcripts.neutral")}</span>
                  </div>
                  
                  <div className="flex items-center mt-1">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-red-400 rounded-full" style={{ width: '7%' }}></div>
                    </div>
                    <span className="ml-2 text-xs text-muted-foreground">7% {t("analytics.transcripts.negative")}</span>
                  </div>
                </div>
              </div>
              
              <Link to="/demo">
                <Button className="bg-brand-500 text-white hover:bg-brand-600 transition-all duration-300 shadow-lg shadow-brand-500/20 rounded-lg py-5 sm:py-6 w-full sm:w-auto">
                  {t("analytics.cta")}
                </Button>
              </Link>
              
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mt-6 md:mt-8">
                <div className="flex items-center gap-2">
                  <Check className="text-white bg-brand-500 rounded-full p-1 w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-muted-foreground">{t("analytics.trial")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="text-white bg-brand-500 rounded-full p-1 w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-muted-foreground">{t("analytics.credit")}</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default AnalyticsReporting;
